<template>
  <section class="tool-page page-shell deep-page">
    <div class="section-heading compact-heading">
      <button class="back-link" @click="navigate('home')">← Home</button>
      <span class="eyebrow"><span></span>For decisions that matter</span>
      <h1>Turn the fog into <em>something you can see.</em></h1>
    </div>

    <div class="deep-progress">
      <div v-for="item in 4" :key="item" :class="{ active: step >= item }"><span>{{ step > item ? '✓' : item }}</span><small>{{ ['Options', 'Priorities', 'Score', 'Reflect'][item - 1] }}</small></div>
    </div>

    <div class="deep-card">
      <transition name="slide" mode="out-in">
        <div v-if="step === 1" key="options" class="deep-step">
          <span class="step-label">STEP 1 OF 4</span>
          <h2>What are you deciding?</h2>
          <p>Name it simply. You can make it nuanced later.</p>
          <label class="field-label">YOUR DECISION</label>
          <input v-model.trim="title" class="large-input" placeholder="e.g. Which job offer should I take?" maxlength="120">
          <label class="field-label">YOUR OPTIONS</label>
          <div v-for="(option, index) in options" :key="index" class="option-row deep-option">
            <b>{{ String.fromCharCode(65 + index) }}</b><input v-model.trim="options[index]" :placeholder="`Option ${String.fromCharCode(65 + index)}`" maxlength="60"><button v-if="options.length > 2" @click="removeOption(index)">×</button>
          </div>
          <button v-if="options.length < 5" class="add-button" @click="addOption">＋ Add another option</button>
        </div>

        <div v-else-if="step === 2" key="criteria" class="deep-step">
          <span class="step-label">STEP 2 OF 4</span>
          <h2>What matters most?</h2>
          <p>Give each factor a weight. Together, they must add up to 100%.</p>
          <div v-for="(criterion, index) in criteria" :key="index" class="criterion-row">
            <input v-model.trim="criterion.name" placeholder="e.g. Growth" maxlength="40">
            <input v-model.number="criterion.weight" type="range" min="5" max="80" step="5">
            <strong>{{ criterion.weight }}%</strong>
            <button v-if="criteria.length > 2" @click="criteria.splice(index, 1)">×</button>
          </div>
          <div class="weight-total" :class="{ valid: totalWeight === 100 }"><span>Total weight</span><strong>{{ totalWeight }}%</strong><small>{{ totalWeight === 100 ? 'Ready' : totalWeight < 100 ? `${100 - totalWeight}% left` : `${totalWeight - 100}% over` }}</small></div>
          <button v-if="criteria.length < 6" class="add-button" @click="criteria.push({ name: '', weight: 10 })">＋ Add a factor</button>
        </div>

        <div v-else-if="step === 3" key="scores" class="deep-step score-step">
          <span class="step-label">STEP 3 OF 4</span>
          <h2>Score what you know.</h2>
          <p>Use your honest first instinct. 1 is poor, 10 is excellent.</p>
          <div class="score-table-wrap">
            <table class="score-table">
              <thead><tr><th>Factor</th><th v-for="option in cleanOptions" :key="option">{{ option }}</th></tr></thead>
              <tbody><tr v-for="(criterion, ci) in cleanCriteria" :key="criterion.name"><td><strong>{{ criterion.name }}</strong><small>{{ criterion.weight }}% weight</small></td><td v-for="(option, oi) in cleanOptions" :key="option"><input v-model.number="scores[oi][ci]" type="number" min="1" max="10"><span>/ 10</span></td></tr></tbody>
            </table>
          </div>
          <div class="live-result">
            <span>RATIONAL FRONT-RUNNER</span>
            <strong>{{ winner.name }}</strong>
            <div><i v-for="item in ranking" :key="item.name" :style="{ width: `${item.percent}%` }"><b>{{ item.name }}</b><em>{{ item.score.toFixed(1) }}</em></i></div>
          </div>
        </div>

        <div v-else key="reflect" class="deep-step reflect-step">
          <span class="step-label">STEP 4 OF 4</span>
          <h2>Now ask the human part.</h2>
          <p>The numbers clarify your logic. These questions reveal what the numbers miss.</p>
          <div v-for="(question, index) in questions" :key="index" class="reflection-field">
            <label><span>{{ index + 1 }}</span>{{ question }}</label>
            <textarea v-model.trim="answers[index]" rows="2" placeholder="Write the first honest answer that comes up…" maxlength="500"></textarea>
          </div>
          <div class="final-recommendation">
            <span>YOUR CLEAREST PATH</span>
            <h3>{{ winner.name }}</h3>
            <p>Based on your weighted priorities, this option leads with {{ winner.score.toFixed(1) }} out of 10. Treat this as a clear signal, not a command.</p>
          </div>
        </div>
      </transition>

      <div class="deep-actions">
        <button v-if="step > 1" class="soft-button" @click="step--">← Back</button>
        <span></span>
        <button v-if="step < 4" class="primary-button" :disabled="!canContinue" @click="next">Continue →</button>
        <div v-else class="final-actions"><button class="soft-button" @click="downloadReport">↓ Download white paper</button><button class="primary-button" @click="saveDecision">Save decision</button></div>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../services/api'

export default {
  name: 'DeepView',
  inject: ['navigate', 'notify'],
  props: { user: { type: Object, default: null } },
  data: () => ({
    step: 1,
    title: '',
    options: ['Option A', 'Option B'],
    criteria: [{ name: 'Growth potential', weight: 35 }, { name: 'Quality of life', weight: 35 }, { name: 'Financial reward', weight: 30 }],
    scores: [[7, 6, 8], [6, 9, 6]],
    questions: [
      'If nobody else had an opinion, which option would you lean toward?',
      'Which choice would your future self be most curious to have tried?',
      'What is the worst realistic outcome — and could you recover from it?',
      'Are you choosing from genuine desire, or to avoid discomfort?'
    ],
    answers: ['', '', '', '']
  }),
  computed: {
    cleanOptions () { return this.options.filter(Boolean) },
    cleanCriteria () { return this.criteria.filter(item => item.name) },
    totalWeight () { return this.criteria.reduce((sum, item) => sum + Number(item.weight || 0), 0) },
    canContinue () {
      if (this.step === 1) return Boolean(this.title && this.cleanOptions.length >= 2)
      if (this.step === 2) return this.cleanCriteria.length >= 2 && this.totalWeight === 100
      return true
    },
    ranking () {
      const max = 10
      return this.cleanOptions.map((name, optionIndex) => {
        const score = this.cleanCriteria.reduce((sum, criterion, criterionIndex) => sum + (Number(this.scores[optionIndex]?.[criterionIndex] || 1) * criterion.weight / 100), 0)
        return { name, score, percent: Math.max(8, score / max * 100) }
      }).sort((a, b) => b.score - a.score)
    },
    winner () { return this.ranking[0] || { name: 'No result yet', score: 0 } }
  },
  methods: {
    addOption () { this.options.push(''); this.scores.push(this.criteria.map(() => 5)) },
    removeOption (index) { this.options.splice(index, 1); this.scores.splice(index, 1) },
    next () {
      if (!this.canContinue) return
      if (this.step === 2) this.normalizeScores()
      this.step++
      window.scrollTo({ top: 180, behavior: 'smooth' })
    },
    normalizeScores () {
      this.cleanOptions.forEach((_, oi) => {
        if (!this.scores[oi]) this.$set(this.scores, oi, [])
        this.cleanCriteria.forEach((__, ci) => {
          if (!this.scores[oi][ci]) this.$set(this.scores[oi], ci, 5)
        })
      })
    },
    reportText () {
      const lines = [
        'CALMDECIDE — DECISION WHITE PAPER', '', this.title, `Created ${new Date().toLocaleDateString()}`, '',
        'RATIONAL RANKING', ...this.ranking.map((item, i) => `${i + 1}. ${item.name}: ${item.score.toFixed(1)} / 10`), '',
        'PRIORITIES', ...this.cleanCriteria.map(item => `• ${item.name}: ${item.weight}%`), '',
        'REFLECTIONS', ...this.questions.flatMap((question, i) => [`${i + 1}. ${question}`, this.answers[i] || 'No answer recorded.', '']),
        'CLEAR SIGNAL', `${this.winner.name} is the rational front-runner. This is a signal, not a command. Notice whether the result creates relief or resistance — both are useful information.`, '',
        'calmdecide.com — Less overthinking. More living.'
      ]
      return lines.join('\n')
    },
    downloadReport () {
      const blob = new Blob([this.reportText()], { type: 'text/plain;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `calmdecide-${this.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'decision'}.txt`
      link.click(); URL.revokeObjectURL(link.href)
    },
    async saveDecision () {
      if (!this.user) { this.$emit('require-auth'); return }
      try {
        await api.saveDecision({
          title: this.title, decision_type: 'deep', chosen_option: this.winner.name, options: this.cleanOptions,
          criteria: this.cleanCriteria, scores: this.scores, reflections: this.questions.map((q, i) => ({ question: q, answer: this.answers[i] })),
          time_saved: 45
        })
        this.$emit('saved')
      } catch (error) { this.notify(error.message) }
    }
  }
}
</script>
